/**
 * Parses polygon_geo_zone value from string or object.
 */
export function parseGeoZone(value) {
    if (!value || value === "") {
        return null;
    }

    if (typeof value === "string") {
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    }

    return value;
}

function closeRing(coordinates) {
    if (!coordinates?.length) {
        return coordinates;
    }

    const first = coordinates[0];
    const last = coordinates[coordinates.length - 1];

    if (first[0] === last[0] && first[1] === last[1]) {
        return coordinates;
    }

    return [...coordinates, first];
}

function latLngPointsToRing(points) {
    return closeRing(points.map(({ lat, lng }) => [lng, lat]));
}

function createCircleRing(center, radiusMeters, steps = 64) {
    const { lat, lng } = center;
    const earthRadius = 6378137;
    const coords = [];

    for (let i = 0; i <= steps; i += 1) {
        const angle = (i / steps) * 2 * Math.PI;
        const dx = radiusMeters * Math.cos(angle);
        const dy = radiusMeters * Math.sin(angle);
        const dLat = (dy / earthRadius) * (180 / Math.PI);
        const dLng =
            (dx / (earthRadius * Math.cos((lat * Math.PI) / 180))) *
            (180 / Math.PI);
        coords.push([lng + dLng, lat + dLat]);
    }

    return coords;
}

function ringToLatLngPoints(ring) {
    if (!ring?.length) {
        return null;
    }

    const points = ring.map(([lng, lat]) => ({ lat, lng }));

    if (points.length > 1) {
        const first = points[0];
        const last = points[points.length - 1];
        if (first.lat === last.lat && first.lng === last.lng) {
            points.pop();
        }
    }

    return points.length >= 3 ? points : null;
}

/**
 * Converts polygon_geo_zone data to a Terra Draw GeoJSON feature for addFeatures().
 * Accepts the backend array format [{lat, lng}, ...] and legacy circle/rectangle objects.
 */
export function geoZoneToTerraDrawFeature(geoZone) {
    const parsed = parseGeoZone(geoZone);
    if (!parsed) {
        return null;
    }

    if (Array.isArray(parsed)) {
        if (parsed.length < 3) {
            return null;
        }

        return {
            type: "Feature",
            geometry: {
                type: "Polygon",
                coordinates: [latLngPointsToRing(parsed)],
            },
            properties: { mode: "polygon" },
        };
    }

    if (parsed.type === "rectangle") {
        const { north, south, east, west } = parsed;

        return {
            type: "Feature",
            geometry: {
                type: "Polygon",
                coordinates: [
                    closeRing([
                        [west, south],
                        [east, south],
                        [east, north],
                        [west, north],
                    ]),
                ],
            },
            properties: { mode: "rectangle" },
        };
    }

    if (parsed.type === "circle" && parsed.center && parsed.radius != null) {
        return {
            type: "Feature",
            geometry: {
                type: "Polygon",
                coordinates: [createCircleRing(parsed.center, parsed.radius)],
            },
            properties: {
                mode: "circle",
                radius: parsed.radius,
            },
        };
    }

    return null;
}

/**
 * Returns LatLngBoundsLiteral for fitting the map to an existing geo zone.
 */
export function getGeoZoneBounds(geoZone, fallbackCenter) {
    const parsed = parseGeoZone(geoZone);
    if (!parsed) {
        return null;
    }

    if (Array.isArray(parsed)) {
        const lats = parsed.map(point => point.lat);
        const lngs = parsed.map(point => point.lng);

        if (!lats.length) {
            return null;
        }

        return {
            north: Math.max(...lats),
            south: Math.min(...lats),
            east: Math.max(...lngs),
            west: Math.min(...lngs),
        };
    }

    if (parsed.type === "rectangle") {
        return {
            north: parsed.north,
            south: parsed.south,
            east: parsed.east,
            west: parsed.west,
        };
    }

    if (parsed.type === "circle" && parsed.center && parsed.radius != null) {
        const { lat, lng } = parsed.center;
        const radiusDeg = parsed.radius / 111320;
        const lngDelta =
            radiusDeg / Math.max(Math.cos((lat * Math.PI) / 180), 0.01);

        return {
            north: lat + radiusDeg,
            south: lat - radiusDeg,
            east: lng + lngDelta,
            west: lng - lngDelta,
        };
    }

    if (fallbackCenter?.lat != null && fallbackCenter?.lng != null) {
        const padding = 0.05;
        return {
            north: fallbackCenter.lat + padding,
            south: fallbackCenter.lat - padding,
            east: fallbackCenter.lng + padding,
            west: fallbackCenter.lng - padding,
        };
    }

    return null;
}

/**
 * Converts a Terra Draw feature to the polygon_geo_zone format used by the backend.
 * Always returns [{lat, lng}, ...] — the only format accepted by the API.
 */
export function terraDrawFeatureToGeoZone(feature) {
    if (!feature?.geometry) {
        return null;
    }

    const { geometry } = feature;

    if (geometry.type === "Polygon") {
        const ring = geometry.coordinates?.[0];
        return ringToLatLngPoints(ring);
    }

    if (geometry.type === "LineString") {
        const points = geometry.coordinates.map(([lng, lat]) => ({ lat, lng }));
        return points.length >= 3 ? points : null;
    }

    return null;
}

/**
 * Returns drawable features from a Terra Draw snapshot, excluding UI helper points.
 */
export function getDrawableFeatures(snapshot) {
    return snapshot.filter(
        feature =>
            !feature.properties?.midPoint &&
            !feature.properties?.selectionPoint &&
            !feature.properties?.closingPoint &&
            !feature.properties?.snappingPoint &&
            !feature.properties?.coordinatePoint,
    );
}
